import {
  getAddressFilterOptions,
  getPlansFilterOptions,
  getSpecialtiesFilterOptions,
  getSubSpecialtiesFilterOptions
} from '@/modules/accredited-establishments/api'
import { establishmentCategories } from '@/modules/accredited-establishments/api/types'
import {
  toAccreditedGender,
  toAppointmentType
} from '@/modules/accredited-establishments/functions/parse-enum-param'
import {
  ACCREDITED_ESTABLISHMENTS_ADDRESS_FILTER,
  ACCREDITED_ESTABLISHMENTS_PLANS_FILTER,
  ACCREDITED_ESTABLISHMENTS_SPECIALTIES_FILTER,
  ACCREDITED_ESTABLISHMENTS_SUB_SPECIALTIES_FILTER
} from '@/global/constants/reactQueryKeys'
import {
  CATEGORIES_PARAMS,
  PLAN_PARAMS,
  SPECIALTY_PARAMS,
  SUB_SPECIALTY_PARAMS,
  GENDER_PARAMS,
  APPOINTMENT_TYPE_PARAMS,
  PROVIDER_NAME_PARAMS,
  ADDRESS_SEARCH_PARAMS,
  ADDRESS_PARAMS,
  DEFAULT_PLACE_ID
} from '@/modules/accredited-establishments/constants/accredited-establishments'
import { Query } from '@/global/types/serverSideProps'
import {
  FetchFiltersParams,
  FetchFiltersResponse,
  FetchSubSpecialtiesParams,
  GetQueryParamsResponse,
  ValidateParamsProps,
  ValidateParamsResponse
} from './prefetchAccreditedEstablishmentsList.types'

const getQueryParams = (searchParams: Query): GetQueryParamsResponse => {
  const planParam = searchParams[PLAN_PARAMS] as string | undefined
  const categoriesParam = (
    searchParams[CATEGORIES_PARAMS] as string | undefined
  )?.split(',')
  const specialtyParam = searchParams[SPECIALTY_PARAMS] as string | undefined
  const subSpecialtyParam = searchParams[SUB_SPECIALTY_PARAMS] as
    | string
    | undefined
  const genderParam = toAccreditedGender(searchParams[GENDER_PARAMS])
  const appointmentTypeParam = toAppointmentType(
    searchParams[APPOINTMENT_TYPE_PARAMS]
  )
  const providerNameParam = searchParams[PROVIDER_NAME_PARAMS] as
    | string
    | undefined
  const addressSearchParam = searchParams[ADDRESS_SEARCH_PARAMS] as
    | string
    | undefined
  const addressParam = searchParams[ADDRESS_PARAMS] as string | undefined

  return {
    planParam,
    categoriesParam,
    specialtyParam,
    subSpecialtyParam,
    genderParam,
    appointmentTypeParam,
    providerNameParam,
    addressSearchParam,
    addressParam
  }
}

const fetchFilters = async ({
  queryClient,
  filtersParams
}: FetchFiltersParams): Promise<FetchFiltersResponse> => {
  const { addressSearchParam, addressParam } = filtersParams

  const queriesToFetch = [
    queryClient.fetchQuery({
      queryKey: ACCREDITED_ESTABLISHMENTS_PLANS_FILTER,
      queryFn: getPlansFilterOptions
    }),
    queryClient.fetchQuery({
      queryKey: ACCREDITED_ESTABLISHMENTS_SPECIALTIES_FILTER,
      queryFn: getSpecialtiesFilterOptions
    })
  ]

  const hasAddressFilter = !!addressSearchParam && !!addressParam
  if (hasAddressFilter) {
    queriesToFetch.push(
      queryClient.fetchQuery({
        queryKey: [
          ...ACCREDITED_ESTABLISHMENTS_ADDRESS_FILTER,
          addressSearchParam
        ],
        queryFn: () => getAddressFilterOptions({ search: addressSearchParam })
      })
    )
  }

  const result = await Promise.allSettled(queriesToFetch)
  const plans = result[0].status === 'fulfilled' ? result[0].value : undefined
  const specialties =
    result[1].status === 'fulfilled' ? result[1].value : undefined
  const addresses = hasAddressFilter
    ? result[2].status === 'fulfilled'
      ? result[2].value
      : undefined
    : undefined

  return { plans, specialties, addresses }
}

/*
 * Runs after `validateParams` because the options depend on the specialty that
 * survived validation, and only when the link carries a subspecialty — a plain
 * link must not pay for a request it cannot use.
 */
const fetchValidSubSpecialtyId = async ({
  queryClient,
  specialtyId,
  subSpecialtyParam
}: FetchSubSpecialtiesParams): Promise<string | undefined> => {
  if (!subSpecialtyParam || !specialtyId) return undefined

  try {
    const subSpecialties = await queryClient.fetchQuery({
      queryKey: [
        ...ACCREDITED_ESTABLISHMENTS_SUB_SPECIALTIES_FILTER,
        specialtyId
      ],
      queryFn: () => getSubSpecialtiesFilterOptions(specialtyId)
    })

    return subSpecialties.items.find(item => item.value === subSpecialtyParam)
      ?.value
  } catch {
    /* The client resolves the param again on mount; the page still renders. */
    return undefined
  }
}

const validateParams = ({
  params,
  filtersOptions
}: ValidateParamsProps): ValidateParamsResponse => {
  const { planParam, specialtyParam, addressParam, categoriesParam } = params
  const { plans, specialties, addresses } = filtersOptions

  const validPlan = plans?.items?.find(p => p.value === planParam)?.value
  const validSpecialty = specialties?.items?.find(
    s => s.value === specialtyParam
  )?.value
  const validAddress = addresses?.items?.find(a => a.value === addressParam)
    ?.value
  const validCategories = establishmentCategories.filter(
    c => categoriesParam?.includes(c)
  )

  const categoryIds = validCategories.length ? validCategories : undefined
  const specialtyId = validCategories.includes('SPECIALIST')
    ? validSpecialty
    : undefined
  const planId = validPlan
  const placeId = validAddress ? validAddress : DEFAULT_PLACE_ID

  return {
    categoryIds,
    specialtyId,
    planId,
    placeId
  }
}

export {
  getQueryParams,
  fetchFilters,
  fetchValidSubSpecialtyId,
  validateParams
}
